import { CommonModule } from "@angular/common";
import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectorRef,
  Component,
  type ElementRef,
  OnDestroy,
  effect,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from "@angular/core";
import { Router } from "@angular/router";
import { IonicModule } from "@ionic/angular";
import { BannerTemplates, I_NewCarousel } from "@models/I_NewCarousel";
import { I_UserInfo } from "@models/I_UserInfo";
import { GlobalVarsModel } from "@models/ModelGlobalsVar";
import { DataUserMSOService } from "@mso-services/dataUserMSO.services";
import { CountDownFormatPipe } from "@pipes/mso/countDownFormat.pipe";
import { NewBaseService } from "@providers/newBase.service";
import { DeviceService } from "@services/device.service";
import { CASINO_PATHS } from "@shared-constants/routes";
import { Utils } from "@shared-utils/Utils";
import { NewTicketFacade } from "@sports-features/ticket/stores";
import { Subscription } from "rxjs";
import { Swiper, SwiperOptions } from "swiper";
import { SwiperContainer } from "swiper/element";
import { CodereCountdownBannerTemplateComponent } from "../codere-countdown-banner-template/codere-countdown-banner-template.component";
import { CodereCountdownComponent, CountDown, INIT_COUNT } from "../codere-countdown/codere-countdown.component";
import { CodereSliderContainer } from "../codere-slider-container/codere-slider-container";
@Component({
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: "codere-slider-csv",
  templateUrl: "./codere-slider-csv.html",
  standalone: true,
  styleUrls: ["./codere-slider-csv.scss"],
  imports: [
    CodereSliderContainer,
    CommonModule,
    IonicModule,
    CodereCountdownComponent,
    CodereCountdownBannerTemplateComponent,
    CountDownFormatPipe,
  ],
})
export class CodereSliderCsvComponent implements OnDestroy {
  auxBanners = signal<I_NewCarousel[]>([]);
  banners = input.required<I_NewCarousel[]>();
  swiper = signal<Swiper>(undefined);
  swiperRef = viewChild<ElementRef<SwiperContainer>>("swiperCSV");
  bullets = viewChild<ElementRef<HTMLElement>>("bullets");
  userInfo: I_UserInfo;
  globalVars!: GlobalVarsModel;
  isDesktop: boolean;
  config: SwiperOptions;
  countDown: CountDown = { ...INIT_COUNT };
  subscription$: Subscription;

  private newBaseService = inject(NewBaseService);
  private router = inject(Router);
  private dataUserMSOService = inject(DataUserMSOService);
  private deviceService = inject(DeviceService);
  private newTicketFacade = inject(NewTicketFacade);
  private cdRef = inject(ChangeDetectorRef);
  private utils = inject(Utils);

  constructor() {
    this.subscription$ = this.newBaseService.getVars.subscribe((data: GlobalVarsModel) => {
      this.globalVars = data;
    });
    this.isDesktop = this.deviceService.isDesktop();

    effect(
      () => {
        this.userInfo = this.dataUserMSOService.userInfo();
        untracked(() => {
          this.auxBanners.update(() => this.banners().filter((b) => this.canShowSlider(b)));
          if (this.swiper() && this.auxBanners()) {
            this.swiper().slideTo(0);
            this.cdRef.detectChanges();
            this.swiper().update();
            this.swiper().slideReset();
          }
        });
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.swiperRef()) {
          this.config = {
            loop: true,
            slidesPerView: "auto",
            autoplay: {
              delay: 5000,
            },
            pagination: {
              el: this.bullets()?.nativeElement,
              type: "bullets",
              clickable: true,
            },
            observer: true,
          };

          const swiperEl = Object.assign(this.swiperRef().nativeElement, this.config);
          swiperEl.initialize();
          this.swiper.update(() => this.swiperRef().nativeElement.swiper);
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnDestroy(): void {
    this.subscription$?.unsubscribe();
  }

  btnLeft() {
    this.swiper()?.slidePrev();
  }

  btnRight() {
    this.swiper()?.slideNext();
  }

  canShowSlider(slider: I_NewCarousel): boolean {
    let canShow = false;
    let dateStart: string = slider.startDate;
    let dateEnd: string = slider.endDate;
    if (slider.fromHour) {
      dateStart += ` ${slider.fromHour}`;
    }
    if (slider.toHour) {
      dateEnd += ` ${slider.toHour}`;
    }

    if (this.canShowByDates(dateStart, dateEnd)) {
      switch (slider.showLogged) {
        case "ShowForLoggedOut":
          canShow = !this.globalVars.user.logged;
          break;
        case "ShowForLoggedInAndLoggedOut":
          canShow = true;
          break;
        case "ShowForLoggedIn":
          canShow = this.globalVars.user.logged;
          break;
        case "ShowForLoggedInEligibleUser":
          canShow = this.ShowForLoggedInEligibleUser(slider.vipLevel);
          break;
        case "ShowForLoggedInNotEligibleUser":
          canShow = this.ShowForLoggedInNotEligibleUser();
          break;
      }
    }
    return canShow;
  }

  ShowForLoggedInNotEligibleUser(): boolean {
    let canShow = false;

    if (this.globalVars.user.logged) {
      if (this.userInfo) {
        canShow = !this.userInfo.eligibilityStatus;
      }
    }

    return canShow;
  }

  canShowByDates(start: string, end: string): boolean {
    const dateStart: number = new Date(start).getTime();
    const dateEnd: number = new Date(end).getTime();
    const dateNow: number = new Date().getTime();

    return dateStart <= dateNow && dateEnd >= dateNow;
  }

  ShowForLoggedInEligibleUser(vipLevel: string): boolean {
    let canShow = false;
    if (this.globalVars.user.logged) {
      const valueCookieObj: I_UserInfo = this.userInfo;
      if (valueCookieObj) {
        const isVipEstado = valueCookieObj.vipEstado && valueCookieObj.vipEstado.toLowerCase() === "yes";
        switch (true) {
          case vipLevel === "NO-VIP-LEVEL" &&
            ((!this.globalVars.FEATURES.MSO_checkElegibleBanners &&
              (!valueCookieObj.vipLevel || valueCookieObj.vipLevel === "")) ||
              !isVipEstado):
            canShow = true;
            break;
          case vipLevel === "ALL-VIP-LEVELS" &&
            ((!this.globalVars.FEATURES.MSO_checkElegibleBanners &&
              valueCookieObj.vipLevel &&
              valueCookieObj.vipLevel !== "") ||
              isVipEstado):
            canShow = true;
            break;
          case (!this.globalVars.FEATURES.MSO_checkElegibleBanners || isVipEstado) &&
            vipLevel.includes(valueCookieObj.vipLevel?.toUpperCase()):
            canShow = true;
            break;
          case vipLevel.toLowerCase() === "under26" &&
            !isVipEstado &&
            valueCookieObj.vipLevel?.toLowerCase() === "under26":
            canShow = true;
            break;
          case (!this.globalVars.FEATURES.MSO_checkElegibleBanners || isVipEstado) &&
            vipLevel.toLowerCase() === "over26" &&
            valueCookieObj.vipLevel?.toLowerCase() !== "under26":
            canShow = true;
            break;
        }
      }
    }
    return canShow;
  }

  clickSlide(slide: I_NewCarousel): void {
    switch (slide.typeOfTemplate) {
      case BannerTemplates.IMG:
      case BannerTemplates.TEXT:
      case BannerTemplates.CASINO:
      case BannerTemplates.SLOTS:
      case BannerTemplates.OMNICHANNEL:
      case BannerTemplates.LIVECASINO:
        if (slide.simpleImageTemplateOrTextLink.startsWith("http")) {
          this.utils.openExternalLinkNative(slide.simpleImageTemplateOrTextLink);
        } else {
          if (
            slide.simpleImageTemplateOrTextLink.toLocaleLowerCase().includes("slotspage") ||
            slide.simpleImageTemplateOrTextLink.toLocaleLowerCase().includes("casinopage")
          ) {
            this.goToCasino(slide.simpleImageTemplateOrTextLink.split("?")[1]);
          } else {
            this.router.navigateByUrl(slide.simpleImageTemplateOrTextLink);
          }
        }
        break;
      case BannerTemplates.ONE_ODD:
        this.newTicketFacade.addBetByLink(slide.eventId);
        break;
    }
  }

  listenerContainerEvent($event) {
    this.btnClickSlide($event.slide, $event.type);
  }

  btnClickSlide(slide: I_NewCarousel, event = "") {
    switch (event) {
      case "leftTeam":
        this.newTicketFacade.addBetByLink(slide.leftTeamBetKey);
        break;
      case "rightTeam":
        this.newTicketFacade.addBetByLink(slide.rightTeamBetKey);
        break;
      case "draw":
        this.newTicketFacade.addBetByLink(slide.drawBetKey);
        break;
      case "goTo":
        this.globalVars.rootScope.tryToGoPage(
          `MercadosPage?NodeId=${slide.matchKey}&LeagueName=${slide.leagueName}&Name=${slide.leftTeam} - ${
            slide.rightTeamName
          }&Sporthandle=${slide.sportTypeName}&SportName=${this.getSportName(
            slide.sportTypeName,
          )}&StartDate=/Date(${this.getDateFromString(slide.matchHour).getTime()})/'`,
        );
        break;
    }
  }

  getSportName(sportHandle = "") {
    let sportName = "";
    switch (sportHandle) {
      case "billard":
        sportName = "Billar";
        break;
      case "esports":
        sportName = "Esports";
        break;
      case "golf":
        sportName = "Golf";
        break;
      case "handball":
        sportName = "Balonmano";
        break;
      case "motor":
        sportName = "Motor";
        break;
      case "snooker":
        sportName = "Snooker";
        break;
      case "soccer_indoor":
        sportName = "Futbol Sala";
        break;
      case "soccer":
        sportName = "Futbol";
        break;
      case "basketball":
        sportName = "Baloncesto";
        break;
      case "darts":
        sportName = "Dardos";
        break;
      case "rugby":
        sportName = "Rugby Union";
        break;
      case "baseball":
        sportName = "Baseball";
        break;
      case "futsal":
        sportName = "Futsal";
        break;
      case "ice_hockey":
        sportName = "Hockey sobre hielo";
        break;
      case "american_football":
        sportName = "Futbol Americano";
        break;
      case "badminton":
        sportName = "Badminton";
        break;
      case "beach_soccer":
        sportName = "Futbol Playa";
        break;
      case "beach_volleyball":
        sportName = "Voley Playa";
        break;
      case "table_tennis":
        sportName = "Tenis de mesa";
        break;
      case "volleyball":
        sportName = "Voleyball";
        break;
      case "tennis":
        sportName = "Tenis";
        break;
    }
    return sportName;
  }
  getDateFromString(dateString: string) {
    const fecchaString: string = dateString.split(" ")[0];
    const horaString: string = dateString.split(" ")[1];
    const newDate: Date = new Date(
      Number(fecchaString.split("/")[2]),
      Number(fecchaString.split("/")[1]) - 1,
      Number(fecchaString.split("/")[0]),
    );
    newDate.setHours(Number(horaString.split(":")[0]));
    newDate.setMinutes(Number(horaString.split(":")[1]));
    newDate.setSeconds(Number(horaString.split(":")[2]));
    return newDate;
  }

  isTeamMach(slide: I_NewCarousel) {
    return (
      slide.typeOfTemplate === BannerTemplates.TEAMS_DRAW ||
      slide.typeOfTemplate === BannerTemplates.TEAMS_NO_DRAW ||
      slide.typeOfTemplate === BannerTemplates.TENNIS
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  goToCasino(params: string) {
    this.router.navigate([`/${CASINO_PATHS.BASE}/${CASINO_PATHS.CasinoPage}`], {
      queryParams: this.formatParams(params),
    });
  }

  formatParams(params: string) {
    const paramObj = {};
    if (params) {
      const paramet: string[] = params.split("&");
      paramet.forEach((value) => {
        const valueParam = value.split("=");
        paramObj[valueParam[0]] = valueParam[1];
      });
    }

    return paramObj;
  }
  canShowCountDown(slider: I_NewCarousel) {
    if (slider.countdownEnd && slider.countdownStart) {
      const dateEnd = new Date(slider.countdownEnd).getTime();
      const dateStart = new Date(slider.countdownStart).getTime();
      const dateNow = new Date().getTime();

      return dateEnd > dateStart && dateNow >= dateStart;
    }

    return false;
  }

  canShowContainer(banner: I_NewCarousel) {
    const { typeOfTemplate } = banner;
    return (
      typeOfTemplate !== BannerTemplates.IMG &&
      typeOfTemplate !== BannerTemplates.CASINO &&
      typeOfTemplate !== BannerTemplates.SLOTS &&
      typeOfTemplate !== BannerTemplates.LIVECASINO &&
      typeOfTemplate !== BannerTemplates.OMNICHANNEL
    );
  }

  formatStylesCountdown(banner: I_NewCarousel) {
    const defaultValue = String(0);
    const styles = {
      top: defaultValue,
      bottom: defaultValue,
      left: defaultValue,
      right: defaultValue,
    };

    const bannerStyleString = (
      (this.isDesktop ? banner.countdownPositionDesktop : banner.countdownPositionMobile) ?? ""
    ).toLowerCase();

    const bannerStyles = bannerStyleString.split("|").map((value) => value.split(":"));

    Object.keys(styles).forEach((key) => {
      const newStyle = bannerStyles.find((style) => style[0] === key);
      if (newStyle) {
        styles[key] = newStyle[1] ?? defaultValue;
      }
    });

    return styles;
  }
}
